import React from 'react';
import { useTheme } from '../../../contexts/ThemeContext';

interface Props {
  personaId: string;
  persona: any;
}

const CapabilitiesProbes: React.FC<Props> = ({ personaId, persona }) => {
  const { darkMode } = useTheme();

  const capabilities = persona?.capabilities || persona?.data?.capabilities || {};
  const capEntries = Object.entries(capabilities).filter(([_, v]) => typeof v === 'number') as [string, number][];

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Capability Bars */}
      <div className={`rounded-xl border p-5 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Capability Scores</h3>
        {capEntries.length === 0 ? (
          <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No capabilities extracted yet.</p>
        ) : (
          <div className="space-y-3">
            {capEntries.map(([key, val]) => (
              <div key={key}>
                <div className="flex justify-between text-xs mb-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{key}</span>
                  <span className={darkMode ? 'text-gray-300' : 'text-gray-700'}>{val}/10</span>
                </div>
                <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-purple-500 to-blue-500 transition-all"
                    style={{ width: `${(val / 10) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Probe Summary */}
      <div className={`rounded-xl border p-5 ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`}>
        <h3 className={`text-sm font-semibold mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Probe Analytics</h3>
        {(() => {
          const ep = persona?.data?.data?.engineering_profile?.current_state || {};
          const probes = ep.probes || [];
          const totalProbes = probes.length;
          const passed = probes.filter((p: any) => p.last_passed === true).length;
          const failed = probes.filter((p: any) => p.last_passed === false).length;
          const pending = totalProbes - passed - failed;

          if (totalProbes === 0) {
            return (
              <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                No probes run yet. Run probes from the Forge's Probes tab to see analytics here.
              </p>
            );
          }

          const passRate = totalProbes > 0 ? Math.round((passed / totalProbes) * 100) : 0;
          return (
            <div className="space-y-4">
              {/* Pass Rate Bar */}
              <div>
                <div className="flex justify-between text-xs mb-1">
                  <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Pass Rate</span>
                  <span className={passRate >= 80 ? 'text-green-400' : passRate >= 50 ? 'text-amber-400' : 'text-red-400'}>{passRate}%</span>
                </div>
                <div className={`h-2 rounded-full ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} flex overflow-hidden`}>
                  <div className="bg-green-500 h-2" style={{ width: `${(passed / totalProbes) * 100}%` }} />
                  <div className="bg-red-500 h-2" style={{ width: `${(failed / totalProbes) * 100}%` }} />
                </div>
              </div>
              {/* Counts */}
              <div className="flex gap-4 text-xs">
                <span className="text-green-400">{passed} passed</span>
                <span className="text-red-400">{failed} failed</span>
                {pending > 0 && <span className={darkMode ? 'text-gray-500' : 'text-gray-400'}>{pending} pending</span>}
              </div>
              {/* Focus Areas */}
              {(() => {
                const areas = new Map<string, number>();
                probes.forEach((p: any) => {
                  const area = p.focus_area || 'general';
                  areas.set(area, (areas.get(area) || 0) + 1);
                });
                return areas.size > 0 ? (
                  <div>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Focus Areas</p>
                    <div className="flex flex-wrap gap-1">
                      {Array.from(areas.entries()).map(([area, count]) => (
                        <span key={area} className="px-1.5 py-0.5 text-[10px] rounded bg-purple-600/20 text-purple-300">{area} ({count})</span>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default CapabilitiesProbes;
